import { useEffect, useState } from "react";

import { PopulationBasedFundingDeregistrationPopup } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingDeregistrationPopup";
import { PopulationBasedFundingRegistrationPopup } from "@/Common/Components/PopulationBasedFunding/PopulationBasedFundingRegistrationPopup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PopulationBasedFundingFormData } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { PopulationBasedFundingActions } from "@/Common/Services/PopulationBasedFunding/PopulationBasedFundingService";
import { PopulationBasedFundingPatientHistory } from "./PopulationBasedFundingPatientHistory/PopulationBasedFundingPatientHistory";
import { PopulationBasedFundingViewHeader } from "./PopulationBasedFundingPatientHistory/PopulationBasedFundingViewHeader";

import styles from "./PopulationBasedFundingView.module.scss";

enum PopulationBasedFundingPopUpType {
    None,
    Registeration,
    Deregistration,
    RegistrationDeletionWarning,
    FileDeletionWarning
}

interface IPopulationBasedFundingProps {
    patientModel: Patient;
}

function PopulationBasedFundingView(props: IPopulationBasedFundingProps): JSX.Element {
    const [populationBasedFundingCurrentPopUp, setPopulationBasedFundingCurrentPopUp] = useState(PopulationBasedFundingPopUpType.None);
    const [latestActiveEvent, setLatestActiveEvent] = useState(null);
    const { getText } = useTranslation();
    const isPatientRegistered = props.patientModel.PopulationBasedFunding?.getIsPatientRegistered();
    const hasPendingProposal = props.patientModel.PopulationBasedFunding?.getPatientHasPendingProposal();
    const actionLinkLabel = !isPatientRegistered ? getText("PopulationBasedFunding_RegisterPatientLabel") : getText("PopulationBasedFunding_DeregisterPatientLabel");
    const patientRegistrationStatusLabel = isPatientRegistered ? getText("PopulationBasedFunding_RegisteredStatus") : getText("PopulationBasedFunding_NotRegisteredStatus");

    const onActionClickEvent = async (): Promise<void> => {
        if (await !PopulationBasedFundingActions.showBillingLoginfNotLoggedInAsync(() => onActionClickEvent()))
            return;

        setPopulationBasedFundingCurrentPopUp(!isPatientRegistered
            ? PopulationBasedFundingPopUpType.Registeration
            : PopulationBasedFundingPopUpType.Deregistration
        );
    };

    const onSubmitRegistration = async (formData: PopulationBasedFundingFormData): Promise<boolean> => {
        const response = await PopulationBasedFundingActions
            .registerPatient(props.patientModel, formData);

        if (response?.IsSuccess) {
            PopulationBasedFundingActions.pushEventToHistoryAndExtendItem(response.Event, props.patientModel);
            PopulationBasedFundingActions.updateIcon(props.patientModel.InactivableResponse.Id);

            setLatestActiveEvent(response.Event);
            setPopulationBasedFundingCurrentPopUp(PopulationBasedFundingPopUpType.None);
        }

        return response?.IsSuccess ?? false;
    };

    const onSubmitDeregistraiton = async (formData: PopulationBasedFundingFormData): Promise<boolean> => {
        const response = await PopulationBasedFundingActions
            .deregisterPatient(props.patientModel.InactivableResponse.Id, formData);

        if (response?.IsSuccess) {
            PopulationBasedFundingActions.pushEventToHistoryAndExtendItem(response.Event, props.patientModel);
            PopulationBasedFundingActions.updateIcon(props.patientModel.InactivableResponse.Id);

            setLatestActiveEvent(response.Event);
            setPopulationBasedFundingCurrentPopUp(PopulationBasedFundingPopUpType.None);
        }

        return response?.IsSuccess ?? false;
    };

    useEffect(() => {
        $(window).trigger("resize");
    });

    useEffect(() => {
        const fetchLatestActiveEvent = async (): Promise<void> => {
            const response = await PopulationBasedFundingActions.getLatestActiveEvent(props.patientModel.PatientResponse.ContactFacet_id);
            setLatestActiveEvent(response);
            PopulationBasedFundingActions.updateIcon(props.patientModel.PatientResponse.ContactFacet_id);
        };

        fetchLatestActiveEvent();
    }, [props.patientModel.PatientResponse.ContactFacet_id]);

    return (
        <div className={styles.populationBasedFundingViewComponent + " patient-prof scrollable"}>
            <PopulationBasedFundingViewHeader
                actionLinkLabel={actionLinkLabel}
                patientRegistrationStatusLabel={patientRegistrationStatusLabel}
                onActionClickEvent={onActionClickEvent}
                disableActionLink={hasPendingProposal}
            />

            {(populationBasedFundingCurrentPopUp == PopulationBasedFundingPopUpType.Registeration) && (
                <PopulationBasedFundingRegistrationPopup
                    currentStarredPhysician={props.patientModel?.HasDoctor()
                        ? props.patientModel?.GetAttendingDoctorOrSpecialistOnfileIntervenor()?.Employee.toPerson()
                        : null
                    }
                    onClose={() => setPopulationBasedFundingCurrentPopUp(PopulationBasedFundingPopUpType.None)}
                    onTrySubmit={onSubmitRegistration}
                />
            )}

            {(populationBasedFundingCurrentPopUp == PopulationBasedFundingPopUpType.Deregistration) && (
                <PopulationBasedFundingDeregistrationPopup
                    patientModel={props.patientModel}
                    currentStarredPhysician={latestActiveEvent
                        ? cacheStore.GetCliniciansPersonsByContactFacetId(latestActiveEvent?.EmployeeContactFacetId)
                        : null
                    }
                    onClose={() => setPopulationBasedFundingCurrentPopUp(PopulationBasedFundingPopUpType.None)}
                    onTrySubmit={onSubmitDeregistraiton}
                />
            )}

            <PopulationBasedFundingPatientHistory patientModel={props.patientModel} />
        </div>
    );
}

export { IPopulationBasedFundingProps, PopulationBasedFundingView };
